import { useCallback } from 'react';

import { Autocomplete, Box, Chip, Stack, TextField, Tooltip, Typography } from '@mui/material';

import { type HotelOption, useHotelSelector } from './useHotelSelector.js';

type SelectedHotelChipsProps = {
  selected: HotelOption[];
  focused: boolean;
  onRemove: (option: HotelOption) => void;
};

/**
 * Selection rendered inside the input. While the field is focused the chips
 * collapse to a count so the whole width is left for typing the search.
 */
function SelectedHotelChips({ selected, focused, onRemove }: SelectedHotelChipsProps) {
  if (selected.length === 0) return null;

  if (focused) {
    return <Chip label={selected.length} size="small" sx={{ m: 0.25 }} />;
  }

  const [first, ...rest] = selected;

  return (
    <>
      <Tooltip title={first.label}>
        <Chip
          label={first.label}
          size="small"
          sx={{ m: 0.25, maxWidth: 180 }}
          onDelete={() => onRemove(first)}
        />
      </Tooltip>
      {rest.length > 0 && (
        <Tooltip
          title={
            <Stack>
              {rest.map((option) => (
                <Typography key={option.value} variant="caption">
                  {option.label}
                </Typography>
              ))}
            </Stack>
          }
        >
          <Chip label={`+${rest.length} more`} size="small" sx={{ m: 0.25 }} />
        </Tooltip>
      )}
    </>
  );
}

/** Desktop hotel multi-select rendered in the AppBar header. */
export function HotelSelector() {
  const {
    hotelOptions,
    value,
    inputValue,
    setInputValue,
    handleChange,
    handleInputChange,
    handleLoadMore,
    shouldLoadMore,
    isLoading,
  } = useHotelSelector();

  const handleListboxScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      const listbox = event.currentTarget;
      if (!listbox) return;
      const atBottom = listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 8;
      if (atBottom && shouldLoadMore) handleLoadMore();
    },
    [shouldLoadMore, handleLoadMore]
  );

  const handleInputBlur = useCallback(() => {
    setInputValue('');
    handleInputChange(undefined);
  }, [setInputValue, handleInputChange]);

  const handleRemove = useCallback(
    (option: HotelOption) => handleChange(value.filter((o) => o.value !== option.value)),
    [handleChange, value]
  );

  return (
    <Box sx={{ width: 320 }}>
      <Autocomplete<HotelOption, true, false, false>
        disableCloseOnSelect
        fullWidth
        multiple
        filterOptions={(x) => x}
        getOptionLabel={(o) => o.label || o.value || ''}
        inputValue={inputValue}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        loading={isLoading}
        options={hotelOptions}
        renderInput={(params) => (
          <TextField {...params} label="Hotels" size="small" onBlur={handleInputBlur} />
        )}
        renderValue={(selected, _getItemProps, ownerState) => (
          <SelectedHotelChips
            focused={ownerState.focused}
            selected={selected}
            onRemove={handleRemove}
          />
        )}
        slotProps={{ listbox: { onScroll: handleListboxScroll } }}
        sx={{
          '& .MuiAutocomplete-inputRoot': {
            flexWrap: 'nowrap',
            overflow: 'hidden',
          },
        }}
        value={value}
        onChange={(_, val) => handleChange(val)}
        onInputChange={(_, val, reason) => {
          if (reason === 'input') {
            setInputValue(val);
            handleInputChange(val);
          }
          if (reason === 'clear') {
            setInputValue('');
            handleInputChange(undefined);
          }
        }}
      />
    </Box>
  );
}
